/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable import/prefer-default-export */
import { createRef, useState } from "react";
import { Button, Carousel, Icon, Sheet } from "@components";
import classNames from "classnames";
import useFeatureFlag from "src/lib/hooks/useFeatureFlag";

import useVisibility from "../../hooks/useVisibility";
import {
  filterViewports,
  formatMedia,
  formatPlainMedia,
} from "../../lib/components/util";
import { MergedProductVariant } from "../../lib/services/catalog/types";

import { ImageGalleryMedia } from "./components/ImageGalleryMedia";
import selectors from "./selectors";

export const GALLERY_CUTOFF_CONTROL = 4;
export const GALLERY_CUTOFF_VARIANT = 3;

export const shouldBeSmall = (
  index: number,
  length: number,
  isControl: boolean
) => {
  // Control side logic
  if (isControl) {
    const adjustedIndex = index - 1;
    const isThirdInGroup = adjustedIndex % 3 === 2;
    const isSecondInGroupAndNotLastImage =
      adjustedIndex % 3 === 1 && index !== length - 1;

    return isThirdInGroup || isSecondInGroupAndNotLastImage;
  }

  // Variant side logic
  const isFirstInPairOfSmalls = index % 3 === 1;
  const isLastImage = index === length - 1;
  const isNotThirdImage = index % 3 !== 0;

  if (isLastImage && isFirstInPairOfSmalls) return false;

  return isNotThirdImage;
};

/**
 * Displays a grid of clickable images, viewable in a sheet
 */
export function ImageGallery({ variant }: { variant: MergedProductVariant }) {
  const [currentIndex, setCurrentIndex] = useState<number>();
  const { isExpandedGalleryVisible, showExpandedGallery, hideExpandedGallery } =
    useVisibility("ExpandedGallery", false);
  const [initialGridHeight, setInitialGridHeight] = useState<number | null>(
    null
  );

  // TODO: remove after PDP experiment
  const { value } = useFeatureFlag("product-form-appearance");
  const isProductFormControl = value === "legacy";

  const cutoff = isProductFormControl
    ? GALLERY_CUTOFF_CONTROL
    : GALLERY_CUTOFF_VARIANT;

  const gridRef = createRef<HTMLDivElement>();
  const sheetIsOpen = currentIndex !== undefined;

  const formattedDesktopMedia = formatMedia(
    filterViewports(variant, "desktop"),
    "desktop"
  );

  const plainImages = formatPlainMedia(variant);
  const galleryMedia = formattedDesktopMedia || plainImages;
  const shouldCollapse = galleryMedia.length > cutoff;
  const mediaToDisplay =
    !shouldCollapse || isExpandedGalleryVisible
      ? galleryMedia
      : galleryMedia.slice(0, cutoff);

  const toggleExpansion = () => {
    // hard to simulate this in tests
    // istanbul ignore next
    if (!gridRef.current) return;

    // Opening sequence
    if (initialGridHeight === null) {
      const { height } = gridRef.current.getBoundingClientRect();
      setInitialGridHeight(height);
      showExpandedGallery();
      return;
    }

    // Closing sequence
    const { height: currentGridHeight } =
      gridRef.current.getBoundingClientRect();
    hideExpandedGallery();

    const top = -1 * (currentGridHeight - initialGridHeight);
    window.scrollBy({ top });
    setInitialGridHeight(null);
  };

  return (
    <div
      data-current-slide={currentIndex}
      data-testid={selectors.imageGallery}
      className="w-full"
    >
      {/* Image Grid */}
      <div
        ref={gridRef}
        className="grid grid-flow-row grid-cols-2 grid-rows-2 gap-3"
      >
        {mediaToDisplay.map((media, index) => (
          <button
            data-testid={`image-button-${index + 1}`}
            type="button"
            // images will be static
            key={index}
            className={classNames("relative group aspect-[3/2]", {
              "col-span-2 row-span-2": !shouldBeSmall(
                index,
                galleryMedia.length,
                isProductFormControl
              ),
            })}
            onClick={() => setCurrentIndex(index)}
          >
            <ImageGalleryMedia
              media={media}
              objectFit="cover"
              bigScreen={false}
            />
          </button>
        ))}

        {shouldCollapse && (
          <div className="relative col-span-full">
            <div
              className={classNames("relative h-16 pointer-events-none", {
                "opacity-0": isExpandedGalleryVisible,
                "opacity-50": !isExpandedGalleryVisible,
              })}
            >
              <div className="w-full relative aspect-[3/2]">
                <ImageGalleryMedia
                  media={galleryMedia[cutoff]}
                  objectFit="cover"
                  bigScreen={false}
                />
              </div>
            </div>

            <div className="absolute inset-0 overflow-hidden bg-gradient-to-b from-white/60 to-white/75 text-charcoal">
              <Button
                data-testid={selectors.galleryToggleBtn}
                variant="unstyled"
                onClick={toggleExpansion}
                className="absolute inset-0 underline uppercase transition text-2xs hover:opacity-70 text-slate -top-1 underline-offset-8"
              >
                {/* TODO: i18n */}
                {isExpandedGalleryVisible ? "See Less" : "See More"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Sheet */}
      <Sheet
        isOpen={sheetIsOpen}
        side="bottom"
        className="px-8"
        onDismiss={() => setCurrentIndex(undefined)}
      >
        <button
          data-testid={selectors.carouselCloseBtn}
          type="button"
          onClick={() => setCurrentIndex(undefined)}
          className="absolute z-10 px-0 py-1 top-6 right-8 hover:cursor-pointer"
        >
          <Icon name="close" className="w-5 h-5" />
        </button>

        <Carousel
          autoplay={{ enabled: false }}
          initialSlide={currentIndex}
          pagination={{
            clickable: true,
            position: "right",
            type: "fraction",
            outside: true,
          }}
          className="image-gallery"
          navigation={{
            position: "outside",
            spacing: true,
          }}
        >
          {galleryMedia?.map((media, index) => (
            <div key={index} className="h-[80vh] w-full group">
              <ImageGalleryMedia media={media} objectFit="contain" bigScreen />
            </div>
          ))}
        </Carousel>
      </Sheet>
    </div>
  );
}
